import React, {
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Badge, CardBody, Col, Row, Spinner, Table } from "reactstrap";
import { useSearchParams } from "react-router-dom";
import {
  Column,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  RowSelectionState,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import { ApiList } from "types/api.types";

/* -------------------- Debounced Input -------------------- */

const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => setValue(initialValue), [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => onChange(value), debounce);
    return () => clearTimeout(timeout);
  }, [value, onChange, debounce]);

  return (
    <input
      {...props}
      value={value}
      className="form-control search"
      onChange={(e) => setValue(e.target.value)}
    />
  );
};

/* -------------------- Column Filter -------------------- */

const Filter = ({ column }: { column: Column<any, unknown> }) => {
  const columnFilterValue = column.getFilterValue();
  return (
    <DebouncedInput
      type="text"
      value={(columnFilterValue ?? "") as string}
      onChange={(value) => column.setFilterValue(value)}
      placeholder="Search..."
      className="w-100 mt-1"
    />
  );
};

/* -------------------- Props -------------------- */

interface TableContainerProps<T, P extends Record<string, any> = {}> {
  tableInstanceRef?: React.MutableRefObject<any>;
  onSelectionChange?: (rows: T[]) => void;
  columns: any;
  fetchApi: (payload: {
    page: number;
    limit: number;
    search?: string;
  }) => Promise<{
    success: boolean;
    message: string;
    data: ApiList<T[]>;
  }>;

  requestParams?: P;
  refreshRef?: React.RefObject<(() => void) | null>;
  pageSize?: number;
  globalSearch?: boolean;
  tableClass?: string;
  theadClass?: string;
  divClass?: string;
  SearchPlaceholder?: string;
  /** Extra controls rendered to the right of the search box in the toolbar */
  toolbarRight?: React.ReactNode;
  /** Number of active filters — shows a badge and enables the clear button */
  filterCount?: number;
  /** Called when the user clicks the clear-filters button (only shown when filterCount > 0) */
  onFiltersClear?: () => void;
  /** Panel rendered between the toolbar and the table (e.g. a collapsible filter form) */
  filterPanel?: React.ReactNode;
  /** When false, page/limit/search are not synced to the URL (default: true) */
  syncPaginationToUrl?: boolean;
  /** Hide the page-size selector in the footer (default: false) */
  hidePageSize?: boolean;
}

/* -------------------- Table Container -------------------- */

function TableContainer<T>({
  tableInstanceRef,
  columns,
  onSelectionChange,
  fetchApi,
  pageSize = 10,
  globalSearch = true,
  tableClass,
  refreshRef,
  theadClass,
  requestParams,
  divClass,
  SearchPlaceholder = "Search...",
  toolbarRight,
  filterCount = 0,
  onFiltersClear,
  filterPanel,
  syncPaginationToUrl = true,
  hidePageSize = false,
}: TableContainerProps<T>) {
  const [records, setRecords] = useState<T[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPageIndex = syncPaginationToUrl
    ? Math.max(Number(searchParams.get("page") ?? 1) - 1, 0)
    : 0;
  const initialPageSize = syncPaginationToUrl
    ? Number(searchParams.get("limit") ?? pageSize)
    : pageSize;
  const initialSearch = syncPaginationToUrl
    ? (searchParams.get("search") ?? "")
    : "";

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState({
    pageIndex: initialPageIndex,
    pageSize: initialPageSize,
  });
  const isFirstRender = React.useRef(true);
  const [globalFilter, setGlobalFilter] = useState(initialSearch);

  const { pageIndex } = pagination;

  /* -------------------- Fuzzy Filter -------------------- */

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  /* -------------------- Table Setup -------------------- */

  const table = useReactTable({
    data: records,
    columns,

    state: {
      rowSelection,
      columnFilters,
      globalFilter,
      pagination,
    },

    manualPagination: true,
    pageCount: totalPages,

    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,

    filterFns: { fuzzy: fuzzyFilter },

    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,

    globalFilterFn: fuzzyFilter,

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),

    meta: {
      pageIndex,
      pageSize: pagination.pageSize,
    },
  });

  const { getHeaderGroups, getRowModel } = table;

  /* -------------------- API Fetch -------------------- */

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchApi({
        page: pageIndex + 1,
        limit: pagination.pageSize,
        search: globalFilter || undefined,
        ...(requestParams ?? {}),
      });

      if (res.success) {
        setRecords(res.data.rows);
        setTotalItems(res.data.totalItems);
        setTotalPages(res.data.totalPages);
      }
    } catch (err) {
      console.warn("Table fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [fetchApi, pageIndex, pagination.pageSize, globalFilter, requestParams]);

  useEffect(() => {
    if (!requestParams) return;

    setPagination((p) => ({
      ...p,
      pageIndex: 0,
    }));
  }, [requestParams]);

  useImperativeHandle(refreshRef, () => loadData);

  useEffect(() => {
    if (tableInstanceRef) {
      tableInstanceRef.current = table;
    }
  }, [table, tableInstanceRef]);

  useEffect(() => {
    if (!onSelectionChange) return;

    const selected = table.getSelectedRowModel().rows.map((r) => r.original);

    onSelectionChange(selected);
  }, [rowSelection, table, onSelectionChange]);

  /* -------------------- Effects -------------------- */

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setPagination((p) => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 }));
  }, [globalFilter]);

  useEffect(() => {
    if (!syncPaginationToUrl) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(pagination.pageIndex + 1));
        next.set("limit", String(pagination.pageSize));
        const trimmed = String(globalFilter ?? "").trim();
        if (trimmed) {
          next.set("search", trimmed);
        } else {
          next.delete("search");
        }
        return next;
      },
      { replace: true },
    );
  }, [
    syncPaginationToUrl,
    pagination.pageIndex,
    pagination.pageSize,
    globalFilter,
    setSearchParams,
  ]);

  /* -------------------- Pagination Helpers -------------------- */

  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex + 1 < totalPages;

  const start = pageIndex * pagination.pageSize + 1;
  const end = Math.min((pageIndex + 1) * pagination.pageSize, totalItems);

  /* -------------------- Render -------------------- */

  const showTableToolbar = globalSearch || !!toolbarRight;

  return (
    <Fragment>
      {/* Global search toolbar */}
      {showTableToolbar && (
        <Row className="mb-3">
          <CardBody className="border border-dashed border-end-0 border-start-0">
            <div className="d-flex flex-wrap align-items-center gap-2">
              {globalSearch && (
                <div
                  className="search-box"
                  style={{ width: "min(280px, 100%)" }}
                >
                  <DebouncedInput
                    value={globalFilter}
                    onChange={(v) => setGlobalFilter(String(v))}
                    placeholder={SearchPlaceholder}
                  />
                  <i className="bx bx-search-alt search-icon" />
                </div>
              )}
              {toolbarRight}
              {filterCount > 0 && (
                <Badge pill color="primary" style={{ fontSize: "0.7rem" }}>
                  {filterCount > 99 ? "99+" : filterCount}
                </Badge>
              )}
              {onFiltersClear && filterCount > 0 && (
                <button
                  type="button"
                  className="btn btn-link  p-0 text-muted text-decoration-none"
                  onClick={onFiltersClear}
                >
                  Clear filters
                </button>
              )}
            </div>
          </CardBody>
        </Row>
      )}

      {filterPanel}

      {/* Table */}
      <div className={divClass}>
        <Table hover className={tableClass}>
          <thead className={theadClass}>
            {getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => {
                  return (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        cursor: header.column.getCanSort()
                          ? "pointer"
                          : "default",
                      }}
                    >
                      <div className="d-flex align-items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getCanSort() && (
                          <>
                            {header.column.getIsSorted() === "asc" && (
                              <i className="ri-arrow-up-s-line"></i>
                            )}
                            {header.column.getIsSorted() === "desc" && (
                              <i className="ri-arrow-down-s-line"></i>
                            )}
                            {!header.column.getIsSorted() && (
                              <i className="ri-arrow-up-down-line text-muted"></i>
                            )}
                          </>
                        )}
                      </div>
                      {header.column.getCanFilter() && (
                        <Filter column={header.column} />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={getHeaderGroups()[0].headers.length}
                  className="p-0 align-middle"
                  role="status"
                  aria-live="polite"
                  aria-busy="true"
                  aria-label="Loading table data"
                >
                  <div
                    className="d-flex flex-column align-items-center justify-content-center gap-2 text-muted"
                    style={{ minHeight: 200 }}
                  >
                    <Spinner animation="border" color="primary" />
                    <span className="small">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={getHeaderGroups()[0].headers.length}
                  className="text-center text-muted"
                >
                  No records found
                </td>
              </tr>
            ) : (
              getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td
                        key={cell.id}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Footer Pagination */}
      <Row className="align-items-center mt-2 justify-content-between g-3">
        {!hidePageSize ? (
          <Col sm="auto">
            <select
              className="form-select form-select-sm"
              value={pagination.pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                setPagination({
                  pageIndex: 0,
                  pageSize: newSize,
                });
              }}
            >
              {[10, 20, 50, 100, 250].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </Col>
        ) : null}

        <Col sm="auto">
          <div className="text-muted">
            Showing <b>{start}</b>–<b>{end}</b> of <b>{totalItems}</b> results
          </div>
        </Col>

        <Col sm="auto">
          <ul className="pagination mb-0">
            {/* Previous */}
            <li className={`page-item ${!canPreviousPage ? "disabled" : ""}`}>
              <button
                className="page-link"
                disabled={!canPreviousPage}
                onClick={() =>
                  setPagination((p) => ({
                    ...p,
                    pageIndex: p.pageIndex - 1,
                  }))
                }
              >
                <i className="ri-arrow-left-line"></i>
              </button>
            </li>

            {/* Page Numbers */}
            {(() => {
              const visiblePages = 5;
              const half = Math.floor(visiblePages / 2);

              let startPage = Math.max(pageIndex - half, 0);
              let endPage = Math.min(startPage + visiblePages, totalPages);

              if (endPage - startPage < visiblePages) {
                startPage = Math.max(endPage - visiblePages, 0);
              }

              return (
                <>
                  {/* First page shortcut */}
                  {startPage > 0 && (
                    <>
                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() =>
                            setPagination((p) => ({ ...p, pageIndex: 0 }))
                          }
                        >
                          1
                        </button>
                      </li>
                      <li className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    </>
                  )}

                  {/* Visible pages */}
                  {Array.from(
                    { length: endPage - startPage },
                    (_, i) => startPage + i,
                  ).map((page) => (
                    <li key={page} className="page-item">
                      <button
                        className={`page-link ${
                          page === pageIndex ? "active" : ""
                        }`}
                        onClick={() =>
                          setPagination((p) => ({ ...p, pageIndex: page }))
                        }
                      >
                        {page + 1}
                      </button>
                    </li>
                  ))}

                  {/* Last page shortcut */}
                  {endPage < totalPages && (
                    <>
                      <li className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                      <li className="page-item">
                        <button
                          className="page-link"
                          onClick={() =>
                            setPagination((p) => ({
                              ...p,
                              pageIndex: totalPages - 1,
                            }))
                          }
                        >
                          {totalPages}
                        </button>
                      </li>
                    </>
                  )}
                </>
              );
            })()}

            {/* Next */}
            <li className={`page-item ${!canNextPage ? "disabled" : ""}`}>
              <button
                className="page-link"
                disabled={!canNextPage}
                onClick={() =>
                  setPagination((p) => ({
                    ...p,
                    pageIndex: p.pageIndex + 1,
                  }))
                }
              >
                <i className="ri-arrow-right-line"></i>
              </button>
            </li>
          </ul>
        </Col>
      </Row>
    </Fragment>
  );
}

export default TableContainer;
